/**
 * Auth Interceptor (Functional)
 *
 * Adds a Bearer token to outgoing HTTP requests when the user
 * is authenticated via MSAL. Skips auth for asset/config URLs.
 *
 * This interceptor works alongside the MSAL interceptor —
 * the MSAL interceptor handles protected-resource-map token
 * acquisition, while this provides a fallback for any
 * requests that match the API URL but aren't in the map.
 */
import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import { MSAL_SCOPES } from '../../config/auth.config';

/** URL patterns that should skip auth token injection. */
const SKIP_AUTH_PATTERNS = ['/assets/', '.json', '/login', '/logout', '/authorizeuser', '/refreshtoken', '/validatetoken'];

function shouldSkipAuth(url: string): boolean {
  return SKIP_AUTH_PATTERNS.some((pattern) => url.includes(pattern));
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const msalService = inject(MsalService);

  if (shouldSkipAuth(req.url)) {
    return next(req);
  }

  const account = msalService.instance.getActiveAccount();
  if (!account) {
    return next(req);
  }

  return from(
    msalService.instance.acquireTokenSilent({
      scopes: MSAL_SCOPES,
      account,
    }),
  ).pipe(
    switchMap((response) => {
      const authReq = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${response.accessToken}`),
      });
      return next(authReq);
    }),
    catchError(() => next(req)),
  );
};